import { useEffect, useMemo, useState } from "react";
import { useSort, SortSelect } from "@/lib/sort";

const PL_COLS = { name: (l) => l.name, products: (l) => Object.keys(l.prices || {}).length, customers: (l) => l.customers, valid_until: (l) => l.valid_until, active: (l) => !!l.active };
const PLE_COLS = { name: (p) => p.name, size: (p) => Math.min(p.width_mm, p.height_mm) * 1000 + Math.max(p.width_mm, p.height_mm), price: (p) => p.price };
const PL_OPTS = [["name", "Nome"], ["products", "N. prodotti"], ["customers", "N. clienti"], ["valid_until", "Scadenza"], ["active", "Stato"]];
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileDown, Plus, Save, Percent } from "lucide-react";
import { api, eur, downloadFile, errMsg } from "@/lib/api";
import { ConfirmDelete, Field, inp } from "@/components/admin/AdminUi";
import { Switch } from "@/components/ui/switch";

export function AdminPriceLists() {
  const [list, setList] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/admin/price-lists").then((r) => setList(r.data)); }, []);
  const { rows: sortedLists, sort, setSort } = useSort(list, PL_COLS, { key: "name" });
  const create = async () => {
    try {
      const { data } = await api.post("/admin/price-lists", { name: `Nuovo listino ${new Date().toLocaleDateString("it-IT")}`, prices: {}, active: false });
      navigate(`/admin/listini/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-extrabold uppercase">Listini personalizzati</h1>
          <p className="text-sm text-slate-500">Crea un listino, assegnalo ai clienti dalla loro scheda: lo scaricano in PDF o CSV dal loro account.</p>
        </div>
        <button data-testid="price-list-new-btn" onClick={create} className="press h-10 px-4 rounded-md bg-brand-yellow text-brand-navy text-sm font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> Nuovo listino</button>
      </div>
      {list?.length > 1 && <SortSelect options={PL_OPTS} sort={sort} setSort={setSort} testid="price-lists-sort" />}
      <div className="grid md:grid-cols-2 gap-3">
        {sortedLists?.map((l, i) => (
          <Link key={l.id} to={`/admin/listini/${l.id}`} data-testid={`price-list-card-${i}`} className="press rounded-xl border bg-white p-5 hover:border-brand-navy space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className="font-display text-xl font-bold uppercase">{l.name}</span>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${l.active ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}`}>{l.active ? "ATTIVO" : "DISATTIVATO"}</span>
            </div>
            <div className="text-xs text-slate-500">{Object.keys(l.prices || {}).length} prodotti · {l.customers} clienti{l.valid_until ? ` · valido fino al ${new Date(l.valid_until).toLocaleDateString("it-IT")}` : ""}</div>
          </Link>
        ))}
      </div>
      {list?.length === 0 && <p className="text-sm text-slate-500" data-testid="price-lists-empty">Nessun listino. Creane uno per i tuoi rivenditori o clienti abituali.</p>}
    </div>
  );
}

export function AdminPriceListEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pl, setPl] = useState(null);
  const [products, setProducts] = useState([]);
  const [pct, setPct] = useState(10);
  const [dirty, setDirty] = useState(false);
  const [managers, setManagers] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get(`/admin/price-lists/${id}`).then((r) => alive && setPl(r.data)).catch(() => alive && setPl(false));
    api.get("/admin/products").then((r) => alive && setProducts(r.data));
    api.get("/admin/managers").then((r) => alive && setManagers(r.data));
    return () => { alive = false; };
  }, [id]);
  const [q, setQ] = useState("");
  const filtered = useMemo(() => products.filter((p) => `${p.name} ${p.sku || ""}`.toLowerCase().includes(q.toLowerCase())), [products, q]);
  const { rows: prodRows, Th } = useSort(filtered, PLE_COLS);
  const upd = (patch) => { setPl((p) => ({ ...p, ...patch })); setDirty(true); };
  const prices = pl?.prices || {};
  const included = products.filter((p) => prices[p.id] !== undefined).length;
  if (pl === false) return <p className="text-slate-500">Listino non trovato.</p>;
  if (!pl) return <p className="text-slate-500">Caricamento…</p>;

  const setPrice = (pid, v) => {
    const next = { ...prices };
    if (v === null) delete next[pid]; else next[pid] = v;
    upd({ prices: next });
  };
  const applyPct = () => {
    const next = {};
    products.forEach((p) => { next[p.id] = Math.round(p.price * (1 - pct / 100) * 100) / 100; });
    upd({ prices: next });
    toast.success(`Sconto ${pct}% applicato a tutti i prodotti: ora puoi ritoccare i singoli prezzi`);
  };
  const save = async (quiet) => {
    if (Object.values(prices).some((v) => v === "" || Number.isNaN(+v) || +v < 0)) { toast.error("Inserisci un prezzo valido per ogni prodotto selezionato"); return false; }
    try {
      const { data } = await api.put(`/admin/price-lists/${id}`, { name: pl.name, notes: pl.notes || "", valid_until: pl.valid_until || null, active: pl.active, manager_ids: pl.manager_ids || [], prices: Object.fromEntries(Object.entries(prices).map(([k, v]) => [k, +v])) });
      setPl((p) => ({ ...p, ...data })); setDirty(false);
      if (!quiet) toast.success("Listino salvato");
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const del = async () => {
    try { await api.delete(`/admin/price-lists/${id}`); toast.success("Listino eliminato"); navigate("/admin/listini"); } catch (e) { toast.error(errMsg(e)); }
  };
  const dl = async (fmt) => (!dirty || (await save(true))) && downloadFile({ url: `/admin/price-lists/${id}/file`, params: { fmt } }, `listino-${pl.name}.${fmt}`);

  return (
    <div className="space-y-6" data-testid="price-list-editor">
      <Link to="/admin/listini" className="text-sm text-slate-500 flex items-center gap-1 hover:text-brand-navy"><ArrowLeft className="w-4 h-4" /> Listini</Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-4xl font-extrabold uppercase">{pl.name}</h1>
        <div className="flex flex-wrap gap-2">
          <button data-testid="price-list-download-pdf" onClick={() => dl("pdf")} className="press h-10 px-3 rounded-md border border-brand-navy text-sm font-semibold flex items-center gap-2"><FileDown className="w-4 h-4" /> PDF</button>
          <button data-testid="price-list-download-csv" onClick={() => dl("csv")} className="press h-10 px-3 rounded-md border border-brand-navy text-sm font-semibold flex items-center gap-2"><FileDown className="w-4 h-4" /> CSV</button>
          <ConfirmDelete testId="price-list-delete-btn" title="Eliminare il listino?" text="I clienti a cui è assegnato non lo vedranno più." onConfirm={del} />
        </div>
      </div>
      <div className="rounded-xl border bg-white p-5 grid sm:grid-cols-2 gap-4">
        <Field label="Nome listino"><input data-testid="price-list-name-input" className={inp} value={pl.name} onChange={(e) => upd({ name: e.target.value })} /></Field>
        <Field label="Valido fino al (facoltativo)"><input data-testid="price-list-valid-input" type="date" className={inp} value={pl.valid_until || ""} onChange={(e) => upd({ valid_until: e.target.value || null })} /></Field>
        <Field label="Note stampate sul listino" wide><textarea data-testid="price-list-notes-input" rows={2} className={inp + " h-auto py-2"} value={pl.notes || ""} onChange={(e) => upd({ notes: e.target.value })} /></Field>
        <div className="flex items-center gap-3 sm:col-span-2">
          <Switch data-testid="price-list-active-switch" checked={!!pl.active} onCheckedChange={(v) => upd({ active: v })} />
          <span className="text-sm"><b>{pl.active ? "Attivo" : "Disattivato"}</b> · {pl.active ? "i clienti assegnati possono scaricarlo" : "i clienti non lo vedono"}</span>
        </div>
      </div>
      <div className="rounded-xl border bg-white p-5 space-y-2" data-testid="price-list-managers">
        <div className="font-mono text-[11px] uppercase text-slate-500">Assegna a manager</div>
        <p className="text-xs text-slate-500">I manager selezionati possono scaricare questo listino (PDF e CSV, con i prezzi d'acquisto riservati) dal loro pannello, anche se il listino è disattivato per i clienti. Ricordati di salvare.</p>
        <div className="flex flex-wrap gap-2">
          {managers.map((m) => {
            const on = (pl.manager_ids || []).includes(m.id);
            return (
              <label key={m.id} className={`press flex items-center gap-2 h-9 px-3 rounded-full border text-sm cursor-pointer ${on ? "bg-brand-navy text-white border-brand-navy" : "bg-white"}`}>
                <input type="checkbox" data-testid={`price-list-manager-${m.email}`} checked={on} className="accent-brand-yellow w-4 h-4"
                  onChange={(e) => upd({ manager_ids: e.target.checked ? [...(pl.manager_ids || []), m.id] : (pl.manager_ids || []).filter((x) => x !== m.id) })} />
                {m.name || m.email}
              </label>
            );
          })}
          {!managers.length && <span className="text-sm text-slate-500">Nessun manager: creane uno in Area manager.</span>}
        </div>
      </div>
      <div className="rounded-xl border bg-white">
        <div className="p-4 flex flex-wrap items-center justify-between gap-3 border-b">
          <div className="text-sm"><b data-testid="price-list-included-count">{included}</b> di {products.length} prodotti nel listino</div>
          <input data-testid="price-list-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca prodotto o codice…" className={inp + " max-w-xs"} />
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-600">Parti da uno sconto su tutto</span>
            <input data-testid="price-list-pct-input" type="number" min={0} max={90} value={pct} onChange={(e) => setPct(+e.target.value)} className="w-16 h-9 px-2 rounded-md border border-slate-200 text-sm" />
            <button data-testid="price-list-apply-pct-btn" onClick={applyPct} className="press h-9 px-3 rounded-md bg-brand-navy text-white text-xs font-semibold flex items-center gap-1"><Percent className="w-3.5 h-3.5" /> Applica</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase font-mono text-slate-500"><tr><th className="p-3 w-10" /><Th k="name" testid="pl-sort-name">Prodotto</Th><Th k="size" testid="pl-sort-size">Misura</Th><Th k="price" testid="pl-sort-price">Listino base</Th><th className="p-3">Prezzo cliente (€)</th><th className="p-3">Sconto</th></tr></thead>
            <tbody>
              {prodRows.map((p, i) => {
                const v = prices[p.id];
                const on = v !== undefined;
                return (
                  <tr key={p.id} className={`border-t ${on ? "" : "text-slate-400"}`}>
                    <td className="p-3"><input type="checkbox" data-testid={`price-list-include-${i}`} checked={on} onChange={(e) => setPrice(p.id, e.target.checked ? p.price : null)} className="w-4 h-4 accent-brand-navy" /></td>
                    <td className="p-3"><div className="font-semibold">{p.name}</div><div className="text-xs">{p.sku && <span className="font-mono mr-1">{p.sku}</span>}{p.active ? "" : "non attivo nel catalogo"}</div></td>
                    <td className="p-3 font-mono text-xs whitespace-nowrap">{Math.min(p.width_mm, p.height_mm)}×{Math.max(p.width_mm, p.height_mm)}</td>
                    <td className="p-3 font-mono">{eur(p.price)}</td>
                    <td className="p-3"><input data-testid={`price-list-price-${i}`} type="number" step="0.01" min={0} disabled={!on} value={on ? v : ""} onChange={(e) => setPrice(p.id, e.target.value)} className="w-28 h-9 px-2 rounded-md border border-slate-200 text-sm disabled:bg-slate-50" /></td>
                    <td className="p-3 font-mono text-xs">{on && p.price && v !== "" ? `${Math.round((1 - v / p.price) * 1000) / 10}%` : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {pl.customers_list?.length > 0 && (
        <div className="rounded-xl border bg-white p-4 text-sm space-y-1" data-testid="price-list-customers">
          <div className="font-mono text-[11px] uppercase text-slate-500">Clienti con questo listino</div>
          {pl.customers_list.map((c) => <Link key={c.id} to={`/admin/clienti/${c.id}`} className="block text-brand-navy hover:underline">{c.company || c.name} · {c.email}</Link>)}
        </div>
      )}
      <p className="text-xs text-slate-500">Per assegnare il listino apri la scheda del cliente in "Clienti". Il listino è solo un documento: sul sito il cliente paga i prezzi normali. I prodotti non attivi nel catalogo non compaiono nel file.</p>
      <button data-testid="price-list-save-btn" onClick={() => save()} className="press h-11 px-6 rounded-md bg-brand-yellow text-brand-navy font-semibold flex items-center gap-2"><Save className="w-4 h-4" /> Salva listino</button>
    </div>
  );
}
